import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Code, Wrench } from 'lucide-react';
import CopyButton from '../../../../components/CopyButton';
import { palette, withOpacity } from '../../../../utils/designTokens';
import ToolSchemaCard, { normalizeToolSchemaInput } from './ToolSchemaCard';

const toToolName = (tool, schema, index) => {
  if (tool && typeof tool === 'object' && !Array.isArray(tool)) {
    if (tool.name) return tool.name;
    if (tool.tool_name) return tool.tool_name;
  }
  if (schema && typeof schema === 'object') {
    const functionName = schema.function && typeof schema.function === 'object' ? schema.function.name : null;
    if (functionName) return functionName;
    if (schema.name) return schema.name;
  }
  return `Tool ${index + 1}`;
};

const toRawSchemaText = (tool, schema) => {
  if (tool && typeof tool === 'object' && !Array.isArray(tool)) {
    const explicitRawSchema = tool.json_schema || (tool.tool && tool.tool.json_schema) || tool.schema;
    if (typeof explicitRawSchema === 'string') return explicitRawSchema;
  }

  if (!schema) return '';
  if (typeof schema === 'string') return schema;
  try {
    return JSON.stringify(schema);
  } catch (_err) {
    return String(schema);
  }
};

const toRenderedJsonText = (rawText, schema) => {
  if (typeof rawText === 'string' && rawText.trim()) {
    try {
      return JSON.stringify(JSON.parse(rawText), null, 2);
    } catch (_err) {
      // Fallback below
    }
  }

  if (schema && typeof schema === 'object') {
    try {
      return JSON.stringify(schema, null, 2);
    } catch (_err) {
      // Fallback below
    }
  }

  return rawText || '';
};

const ToolSchemasChevron = ({ tools = [], defaultExpanded = false }) => {
  if (!Array.isArray(tools)) {
    throw new Error('ToolSchemasChevron requires tools prop as an array');
  }

  const toolEntries = useMemo(
    () => tools.map((tool, index) => {
      const schema = normalizeToolSchemaInput(tool);
      const name = toToolName(tool, schema, index);
      return {
        key: `${name}-${index}`,
        name,
        schema,
        rawText: toRawSchemaText(tool, schema),
        renderedJsonText: toRenderedJsonText(toRawSchemaText(tool, schema), schema),
      };
    }),
    [tools]
  );

  const toolEntriesKey = useMemo(() => toolEntries.map(entry => entry.key).join('|'), [toolEntries]);
  const defaultExpandedKeys = useMemo(
    () => (defaultExpanded ? toolEntries.map(entry => entry.key) : []),
    [defaultExpanded, toolEntriesKey]
  );

  const [expandedToolKeys, setExpandedToolKeys] = useState(() => (
    new Set(defaultExpandedKeys)
  ));
  const [rawToolKeys, setRawToolKeys] = useState(() => new Set());

  useEffect(() => {
    setExpandedToolKeys(new Set(defaultExpandedKeys));
  }, [defaultExpandedKeys]);

  useEffect(() => {
    setRawToolKeys(new Set());
  }, [toolEntriesKey]);

  const toggleTool = (toolKey) => {
    setExpandedToolKeys((prev) => {
      const next = new Set(prev);
      if (next.has(toolKey)) {
        next.delete(toolKey);
      } else {
        next.add(toolKey);
      }
      return next;
    });
  };

  const toggleRaw = (toolKey) => {
    setRawToolKeys((prev) => {
      const next = new Set(prev);
      if (next.has(toolKey)) {
        next.delete(toolKey);
      } else {
        next.add(toolKey);
      }
      return next;
    });
  };

  if (toolEntries.length === 0) return null;

  return (
    <div className="space-y-2">
      {toolEntries.map((entry) => {
        const isExpanded = expandedToolKeys.has(entry.key);
        const showRaw = rawToolKeys.has(entry.key);
        return (
          <div
            key={entry.key}
            className="rounded border"
            style={{ borderColor: palette.borders.subtle, backgroundColor: palette.neutrals.canvas }}
          >
            <div
              className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5"
              style={{ borderBottom: isExpanded ? `1px solid ${palette.borders.subtle}` : 'none' }}
            >
              <button
                type="button"
                className="flex items-center gap-2 min-w-0 flex-1 text-left"
                onClick={() => toggleTool(entry.key)}
              >
                {isExpanded ? (
                  <ChevronDown size={14} className="text-text-muted flex-shrink-0" />
                ) : (
                  <ChevronRight size={14} className="text-text-muted flex-shrink-0" />
                )}
                <div className="flex items-center gap-1.5 min-w-0">
                  <Wrench size={12} style={{ color: palette.accents.warm.base, flexShrink: 0 }} />
                  <span
                    className="text-xs font-mono font-semibold truncate"
                    style={{ color: palette.accents.warm.base }}
                  >
                    {entry.name}
                  </span>
                </div>
              </button>
              <div className="flex items-center gap-1 ml-2">
                <CopyButton text={entry.renderedJsonText || entry.rawText} size="xs" label={`Copy ${entry.name} schema`} />
                <button
                  type="button"
                  onClick={() => toggleRaw(entry.key)}
                  className="p-1 rounded transition-colors"
                  style={{
                    backgroundColor: showRaw ? withOpacity(palette.accents.primary.base, 19) : 'transparent',
                    color: showRaw ? palette.accents.primary.base : palette.text.muted,
                  }}
                  title={showRaw ? 'Show formatted schema' : 'Show raw schema JSON'}
                >
                  <Code size={14} />
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="px-2.5 py-1.5">
                {showRaw ? (
                  <div
                    className="p-2 rounded border"
                    style={{
                      borderColor: palette.borders.subtle,
                      backgroundColor: palette.neutrals.panelSecondary,
                    }}
                  >
                    <pre
                      className="text-[10px] font-mono whitespace-pre-wrap break-words"
                      style={{ color: palette.support.codeText, margin: 0 }}
                    >
                      {entry.renderedJsonText}
                    </pre>
                  </div>
                ) : (
                  <ToolSchemaCard schema={entry.schema} showHeader={false} framed={false} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ToolSchemasChevron;
